import { useTranslation } from 'react-i18next';
import { Globe, ArrowUpRight } from 'lucide-react';

const footerLinks = {
  spreadsheet: [
    { label: 'Spreadsheet', href: 'https://kaspreadsheet.com', external: true },
    { label: 'Categories', href: '#categories', external: false },
    { label: 'Sellers', href: '#guides', external: false },
  ],
  guides: [
    { label: 'Review', href: '#review', external: false },
    { label: 'Shipping', href: '#guides', external: false },
    { label: 'QC', href: '#guides', external: false },
    { label: 'Warehouse', href: '#guides', external: false },
    { label: 'Safety', href: '#guides', external: false },
  ],
  resources: [
    { label: 'Articles', href: '#guides', external: false },
    { label: 'FAQ', href: '#guides', external: false },
    { label: 'Comparisons', href: '#guides', external: false },
  ],
  company: [
    { label: 'About', href: '#', external: false },
    { label: 'Contact', href: '#', external: false },
    { label: 'Privacy Policy', href: '#', external: false },
    { label: 'Terms of Service', href: '#', external: false },
  ],
};

const languages = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'da', label: 'Dansk' },
];

export default function Footer() {
  const { t, i18n } = useTranslation();

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  return (
    <footer className="w-full bg-foreground-950 text-background-200">
      <div className="w-full px-4 md:px-6 lg:px-8 xl:px-12 py-12 md:py-16">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 md:gap-6 mb-10 md:mb-12">
            <div className="col-span-2 md:col-span-4 lg:col-span-1">
              <a href="/" className="font-heading text-xl font-bold text-background-50 block mb-3">
                {t('site.name')}
              </a>
              <p className="text-sm text-background-400 leading-relaxed mb-4 max-w-xs">
                {t('site.description')}
              </p>
              <div className="flex items-center gap-2 text-sm text-background-400">
                <Globe className="w-4 h-4" />
                <span>{t('footer.language')}:</span>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                      i18n.language === lang.code
                        ? 'bg-primary-600 text-background-50'
                        : 'bg-foreground-900 text-background-400 hover:text-background-200'
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-background-50 uppercase tracking-wider mb-4">
                {t('footer.spreadsheet')}
              </h4>
              <ul className="space-y-2.5">
                {footerLinks.spreadsheet.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      target={link.external ? '_blank' : undefined}
                      rel={link.external ? 'noopener nofollow' : undefined}
                      className="text-sm text-background-400 hover:text-background-100 transition-colors inline-flex items-center gap-1"
                    >
                      {link.label}
                      {link.external && <ArrowUpRight className="w-3 h-3" />}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-background-50 uppercase tracking-wider mb-4">
                {t('footer.guides')}
              </h4>
              <ul className="space-y-2.5">
                {footerLinks.guides.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      target={link.external ? '_blank' : undefined}
                      rel={link.external ? 'noopener nofollow' : undefined}
                      className="text-sm text-background-400 hover:text-background-100 transition-colors inline-flex items-center gap-1"
                    >
                      {link.label}
                      {link.external && <ArrowUpRight className="w-3 h-3" />}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-background-50 uppercase tracking-wider mb-4">
                {t('footer.resources')}
              </h4>
              <ul className="space-y-2.5">
                {footerLinks.resources.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      target={link.external ? '_blank' : undefined}
                      rel={link.external ? 'noopener nofollow' : undefined}
                      className="text-sm text-background-400 hover:text-background-100 transition-colors inline-flex items-center gap-1"
                    >
                      {link.label}
                      {link.external && <ArrowUpRight className="w-3 h-3" />}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-background-50 uppercase tracking-wider mb-4">
                {t('footer.company')}
              </h4>
              <ul className="space-y-2.5">
                {footerLinks.company.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      target={link.external ? '_blank' : undefined}
                      rel={link.external ? 'noopener nofollow' : undefined}
                      className="text-sm text-background-400 hover:text-background-100 transition-colors inline-flex items-center gap-1"
                    >
                      {link.label}
                      {link.external && <ArrowUpRight className="w-3 h-3" />}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-t border-foreground-800 pt-8">
            <p className="text-xs text-background-500 text-center leading-relaxed mb-3">
              {t('footer.disclaimer')}
            </p>
            <p className="text-xs text-background-600 text-center">
              &copy; {new Date().getFullYear()} AllChinaBuySpread.mom. All rights reserved. Last updated: August 2026.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}